import { Typography, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PageLayout, SettingItem, Dropzone, StartStopIcon, DropzoneErrorCodes } from 'shared-ui';
import { FileRejection } from 'react-dropzone';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { DefinitionsBar } from '../definitionsBar/DefinitionsBar';
import { Input } from 'ui/input/Input';
import { Form } from 'ui/form/Form';
import { TimePicker } from 'ui/timePicker/TimePicker';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { TypInterwalu, RcpFiltr } from 'api/autoExport/autoExport.enum';
import { SYSTEM_EVENT_EXPORT_FORMATS } from '../eventsExportFormats/EventsExportFormats.systemFormats';
import { AddEventExportFormatPageModeEnum } from '../eventsExportFormats/addEventExportFormat/AddEventExportFormat.enum';
import { AppRouteWithParams } from 'routing/AppRoute.enum';

import { AutomationFormBody, AutomationProps, PASSWORD_PLACEHOLDER } from './Automation.types';
import * as Styled from './Automation.styles';

export const Automation = ({
  onSubmit,
  isLoading,
  isEditing,
  isActive,
  lastError,
  isConfigComplete,
  isLifecycleLoading,
  onStartExport,
  onStopExport,
  onEditClick,
  onCancelClick,
  privateKeyFile,
  onPrivateKeyDrop,
  onPrivateKeyRemove,
  privateKeyError,
  setPrivateKeyError,
  isValidatingFile,
  hasPassword,
  hasPrivateKey,
  keyDirty,
}: AutomationProps) => {
  const { formatMessage } = useLocale();
  const navigate = useNavigate();

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    const errorCode = rejectedFiles[0].errors[0].code;
    if (errorCode === DropzoneErrorCodes.FILE_INVALID_TYPE) {
      setPrivateKeyError(formatMessage({ id: AppMessages['definitions.automation.privateKey.invalidFileType'] }));
    }
  };

  const {
    handleSubmit,
    control,
    watch,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useFormContext<AutomationFormBody>();

  const typInterwalu = watch('typInterwalu');
  const selectedFormatTyp = watch('formatTyp');
  const haslo = watch('haslo');
  const isSchedule = typInterwalu === TypInterwalu.HARMONOGRAM;
  const isInterval = typInterwalu === TypInterwalu.INTERWAL;
  const disabled = !isEditing;
  const hasTypedPassword = !!haslo && haslo !== PASSWORD_PLACEHOLDER;
  // WKD-2441: when keyDirty=true the stored key is treated as already removed
  // for credential-presence accounting — only a fresh upload (privateKeyFile)
  // or a typed password counts. Mirrors the BE behaviour after save.
  const effectiveHasPrivateKey = hasPrivateKey && !keyDirty;
  const hasCredential = hasPassword || effectiveHasPrivateKey || !!privateKeyFile || hasTypedPassword;

  const formatTypOptions = SYSTEM_EVENT_EXPORT_FORMATS.map((f) => ({
    value: f.formatTyp,
    label: f.nazwa,
  }));

  const intervalTypeOptions = [
    {
      value: TypInterwalu.INTERWAL,
      label: formatMessage({ id: AppMessages['definitions.automation.intervalType.interval'] }),
    },
    {
      value: TypInterwalu.HARMONOGRAM,
      label: formatMessage({ id: AppMessages['definitions.automation.intervalType.schedule'] }),
    },
  ];

  const rcpFilterOptions = [
    { value: RcpFiltr.TAK, label: formatMessage({ id: AppMessages['definitions.automation.rcp.yes'] }) },
    { value: RcpFiltr.NIE, label: formatMessage({ id: AppMessages['definitions.automation.rcp.no'] }) },
    { value: RcpFiltr.WSZYSTKIE, label: formatMessage({ id: AppMessages['definitions.automation.rcp.all'] }) },
  ];

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['definitions.title'] })}
      renderContent={() => (
        <>
          <DefinitionsBar />

          <SettingItem>
            <Styled.HeaderRow>
              <Typography variant="h4">{formatMessage({ id: AppMessages['definitions.automation.title'] })}</Typography>

              {!isEditing ? (
                <Styled.SmallButton onClick={onEditClick} variant="outlined" disabled={isActive}>
                  {formatMessage({ id: AppMessages['definitions.automation.edit'] })}
                </Styled.SmallButton>
              ) : (
                <Styled.ButtonGroup>
                  <Styled.SmallButton onClick={onCancelClick} variant="text">
                    {formatMessage({ id: AppMessages['definitions.automation.cancel'] })}
                  </Styled.SmallButton>
                  <Styled.SmallButton
                    isLoading={isLoading}
                    variant="outlined"
                    color="primary"
                    disabled={!isValid || !hasCredential}
                    onClick={handleSubmit(onSubmit)}
                  >
                    {formatMessage({ id: AppMessages['definitions.automation.save'] })}
                  </Styled.SmallButton>
                </Styled.ButtonGroup>
              )}
            </Styled.HeaderRow>

            {!isEditing && (
              <Styled.LifecycleRow $hasError={!!lastError}>
                <Styled.LifecycleLabelGroup>
                  <Styled.LifecycleLabel $hasError={!!lastError}>
                    {formatMessage({
                      id: AppMessages[
                        isActive ? 'definitions.automation.lifecycle.stop' : 'definitions.automation.lifecycle.start'
                      ],
                    })}
                  </Styled.LifecycleLabel>
                  {lastError && <Styled.LifecycleErrorText>{lastError}</Styled.LifecycleErrorText>}
                </Styled.LifecycleLabelGroup>

                <Styled.LifecycleButtonWrapper $hasError={!!lastError} $isDisabled={!isConfigComplete}>
                  <Styled.LifecycleButton
                    $hasError={!!lastError}
                    disabled={isLifecycleLoading || !isConfigComplete}
                    onClick={isActive ? onStopExport : onStartExport}
                  >
                    <StartStopIcon isRunning={isActive} />
                  </Styled.LifecycleButton>

                  {isActive && (
                    <Styled.LifecycleLoader $hasError={!!lastError} size={56} thickness={6} variant="indeterminate" />
                  )}
                </Styled.LifecycleButtonWrapper>
              </Styled.LifecycleRow>
            )}

            <Styled.DisabledOverlay $isDisabled={!isEditing}>
              <Styled.Form onSubmit={handleSubmit(onSubmit)}>
                {/* Row 1: Address | Port | Username */}
                <Controller
                  name="adres"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label={formatMessage({ id: AppMessages['definitions.automation.address.label'] })}
                      placeholder={formatMessage({ id: AppMessages['definitions.automation.address.placeholder'] })}
                      errorMessage={errors?.adres?.message}
                      disabled={disabled}
                      required
                    />
                  )}
                />

                <Controller
                  name="port"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label={formatMessage({ id: AppMessages['definitions.automation.port.label'] })}
                      placeholder={formatMessage({ id: AppMessages['definitions.automation.port.placeholder'] })}
                      type="number"
                      errorMessage={errors?.port?.message}
                      disabled={disabled}
                      required
                    />
                  )}
                />

                <Controller
                  name="nazwaUzytkownika"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label={formatMessage({ id: AppMessages['definitions.automation.username.label'] })}
                      placeholder={formatMessage({ id: AppMessages['definitions.automation.username.placeholder'] })}
                      errorMessage={errors?.nazwaUzytkownika?.message}
                      disabled={disabled}
                      required
                    />
                  )}
                />

                {/* Row 2: Password | Directory */}
                <Controller
                  name="haslo"
                  render={({ field }) =>
                    hasPassword && disabled ? (
                      <Input
                        label={formatMessage({ id: AppMessages['definitions.automation.password.label'] })}
                        type="password"
                        value="********"
                        disabled
                      />
                    ) : (
                      <Input
                        {...field}
                        value={field.value || ''}
                        label={formatMessage({ id: AppMessages['definitions.automation.password.label'] })}
                        placeholder={formatMessage({ id: AppMessages['definitions.automation.password.placeholder'] })}
                        errorMessage={errors?.haslo?.message}
                        disabled={disabled}
                        required={!hasPassword && !effectiveHasPrivateKey && !privateKeyFile}
                        helperText={formatMessage({ id: AppMessages['definitions.automation.credential.note'] })}
                        onFocus={() => {
                          if (field.value === PASSWORD_PLACEHOLDER) {
                            field.onChange('');
                          }
                        }}
                      />
                    )
                  }
                />

                <Controller
                  name="katalog"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label={formatMessage({ id: AppMessages['definitions.automation.directory.label'] })}
                      placeholder={formatMessage({ id: AppMessages['definitions.automation.directory.placeholder'] })}
                      errorMessage={errors?.katalog?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <div />

                {/* Row 3: Dropzone - full width */}
                <Styled.DropzoneWrapper>
                  <Styled.DropzoneTitleWrapper>
                    <Typography variant="h5">
                      {formatMessage({ id: AppMessages['definitions.automation.privateKey.title'] })}
                    </Typography>
                    {!hasCredential && <Styled.Required>*</Styled.Required>}
                  </Styled.DropzoneTitleWrapper>
                  {effectiveHasPrivateKey && !privateKeyFile && (
                    <Styled.KeyPresent>
                      {formatMessage({ id: AppMessages['definitions.automation.privateKey.present'] })}
                    </Styled.KeyPresent>
                  )}

                  <Dropzone
                    title=""
                    dropzoneDescription={formatMessage({
                      id: AppMessages['definitions.automation.privateKey.description'],
                    })}
                    file={privateKeyFile}
                    onClickRemoveButton={onPrivateKeyRemove}
                    error={privateKeyError}
                    isLoading={isValidatingFile}
                    rootDropzoneProps={{
                      onDrop: onPrivateKeyDrop,
                      onDropRejected,
                      accept: {
                        'text/plain': ['.txt'],
                      },
                      maxFiles: 1,
                      disabled,
                    }}
                  />
                </Styled.DropzoneWrapper>

                {/* Row 4: File Format | Export File Name | Timestamp checkbox */}
                <Styled.FormatWrapper>
                  <Form.ControlledSelect
                    name="formatTyp"
                    control={control}
                    label={formatMessage({ id: AppMessages['definitions.automation.fileFormat.label'] })}
                    placeholder={formatMessage({ id: AppMessages['definitions.automation.fileFormat.placeholder'] })}
                    options={formatTypOptions}
                    disabled={disabled}
                    required
                  />

                  <Styled.FormatDetailsButton
                    onClick={() => {
                      const values = getValues();
                      const serializable = {
                        ...values,
                        harmonogramCzas1: values.harmonogramCzas1?.toISOString() ?? null,
                        harmonogramCzas2: values.harmonogramCzas2?.toISOString() ?? null,
                        harmonogramCzas3: values.harmonogramCzas3?.toISOString() ?? null,
                      };
                      sessionStorage.setItem('automation-form-draft', JSON.stringify(serializable));
                      navigate(
                        AppRouteWithParams.eventsExportFormats.get({
                          id: selectedFormatTyp,
                          mode: AddEventExportFormatPageModeEnum.PREVIEW,
                        }),
                        { state: { isRedirectFromAutomation: true } },
                      );
                    }}
                    disabled={!selectedFormatTyp || disabled}
                  >
                    <ArrowRightIcon />
                  </Styled.FormatDetailsButton>
                </Styled.FormatWrapper>

                <Controller
                  name="nazwaPliku"
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label={formatMessage({ id: AppMessages['definitions.automation.exportFileName.label'] })}
                      placeholder={formatMessage({
                        id: AppMessages['definitions.automation.exportFileName.placeholder'],
                      })}
                      errorMessage={errors?.nazwaPliku?.message}
                      disabled={disabled}
                      required
                    />
                  )}
                />

                <Controller
                  name="stempelDaty"
                  render={({ field }) => (
                    <Styled.RadioGroupWrapper>
                      <Styled.RadioLabel>
                        {formatMessage({ id: AppMessages['definitions.automation.timestamp.label'] })}
                      </Styled.RadioLabel>
                      <Styled.RadioRow>
                        <RadioGroup
                          row
                          value={field.value ? 'true' : 'false'}
                          onChange={(e) => field.onChange(e.target.value === 'true')}
                        >
                          <FormControlLabel
                            value="true"
                            control={<Radio size="small" />}
                            label={formatMessage({ id: AppMessages['definitions.automation.timestamp.yes'] })}
                            disabled={disabled}
                          />
                          <FormControlLabel
                            value="false"
                            control={<Radio size="small" />}
                            label={formatMessage({ id: AppMessages['definitions.automation.timestamp.no'] })}
                            disabled={disabled}
                          />
                        </RadioGroup>
                      </Styled.RadioRow>
                    </Styled.RadioGroupWrapper>
                  )}
                />

                {/* Row 5: Interval Type + conditional fields */}
                {isInterval || isSchedule ? (
                  <Styled.IntervalRow>
                    <Form.ControlledSelect
                      name="typInterwalu"
                      control={control}
                      label={formatMessage({ id: AppMessages['definitions.automation.intervalType.label'] })}
                      placeholder={formatMessage({ id: AppMessages['definitions.automation.fileFormat.placeholder'] })}
                      options={intervalTypeOptions}
                      disabled={disabled}
                      required
                      withHelperText
                    />

                    {isInterval && (
                      <Controller
                        name="interwalMinuty"
                        render={({ field }) => (
                          <Styled.IntervalInputWrapper>
                            <Input
                              {...field}
                              value={field.value || ''}
                              type="number"
                              placeholder="10-480"
                              errorMessage={errors?.interwalMinuty?.message}
                              disabled={disabled}
                              rightIcon={
                                <span style={{ color: '#9e9e9e', fontSize: 14 }}>
                                  {formatMessage({
                                    id: AppMessages['definitions.automation.intervalMinutes.suffix'],
                                  })}
                                </span>
                              }
                              helperText={formatMessage({
                                id: AppMessages['definitions.automation.intervalMinutes.note'],
                              })}
                            />
                          </Styled.IntervalInputWrapper>
                        )}
                      />
                    )}

                    {isSchedule && (
                      <Styled.SchedulePickersWrapper>
                        <Controller
                          name="harmonogramCzas1"
                          render={({ field }) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                trigger(['harmonogramCzas2', 'harmonogramCzas3']);
                              }}
                              disabled={disabled}
                              placeholder="00:00"
                              errorMessage={errors?.harmonogramCzas1?.message}
                              fullWidth
                            />
                          )}
                        />

                        <Controller
                          name="harmonogramCzas2"
                          render={({ field }) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                trigger(['harmonogramCzas1', 'harmonogramCzas3']);
                              }}
                              disabled={disabled}
                              placeholder="00:00"
                              errorMessage={errors?.harmonogramCzas2?.message}
                              fullWidth
                            />
                          )}
                        />

                        <Controller
                          name="harmonogramCzas3"
                          render={({ field }) => (
                            <TimePicker
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                trigger(['harmonogramCzas1', 'harmonogramCzas2']);
                              }}
                              disabled={disabled}
                              placeholder="00:00"
                              errorMessage={errors?.harmonogramCzas3?.message}
                              fullWidth
                            />
                          )}
                        />
                      </Styled.SchedulePickersWrapper>
                    )}
                  </Styled.IntervalRow>
                ) : (
                  <Form.ControlledSelect
                    name="typInterwalu"
                    control={control}
                    label={formatMessage({ id: AppMessages['definitions.automation.intervalType.label'] })}
                    placeholder={formatMessage({ id: AppMessages['definitions.automation.fileFormat.placeholder'] })}
                    options={intervalTypeOptions}
                    disabled={disabled}
                    required
                  />
                )}

                {/* RCP | Komórka */}
                <Form.ControlledSelect
                  name="rcpFiltr"
                  control={control}
                  label={formatMessage({ id: AppMessages['definitions.automation.rcp.label'] })}
                  options={rcpFilterOptions}
                  disabled={disabled}
                  required
                />

                <Controller
                  name="komorki"
                  render={({ field }) => (
                    <OrganizationsTreeSelect
                      {...field}
                      multiple
                      // WKD-2453: Automation is a management UI (definicje/automatyzacja)
                      // — admin must see ALL komorki regardless of their personal
                      // komorka access. `useFilteredData=false` swaps the user-tree
                      // endpoint for the full /organizacja list.
                      useFilteredData={false}
                      placeholder={formatMessage({
                        id: AppMessages['definitions.automation.organization.placeholder'],
                      })}
                      label={formatMessage({ id: AppMessages['definitions.automation.organization.label'] })}
                      disabled={disabled}
                      inheritChildren
                    />
                  )}
                />

                <div />
              </Styled.Form>
            </Styled.DisabledOverlay>
          </SettingItem>
        </>
      )}
    />
  );
};
